import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Globe, Loader2, AlertTriangle, AlertCircle, Info as InfoIcon,
  CheckCircle2, ShieldAlert, Clock, Gauge, FileCode2, Activity,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import usePageSeo, { useBreadcrumbSchema } from "@/hooks/usePageSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEV_META = {
  critical: { Icon: ShieldAlert,   cls: "bg-rose-50 text-rose-700 border-rose-200",      label: "Critical" },
  high:     { Icon: AlertTriangle, cls: "bg-orange-50 text-orange-700 border-orange-200", label: "High" },
  medium:   { Icon: AlertCircle,   cls: "bg-amber-50 text-amber-700 border-amber-200",   label: "Medium" },
  low:      { Icon: InfoIcon,      cls: "bg-sky-50 text-sky-700 border-sky-200",         label: "Low" },
  info:     { Icon: InfoIcon,      cls: "bg-slate-50 text-slate-600 border-slate-200",   label: "Info" },
};

const SEV_ORDER = ["critical", "high", "medium", "low", "info"];

function Finding({ f }) {
  const meta = SEV_META[f.severity] || SEV_META.info;
  return (
    <div
      data-testid={`seo-finding-${f.check_type}`}
      className={`border rounded-xl p-4 ${meta.cls}`}
    >
      <div className="flex items-center gap-2">
        <meta.Icon size={16} />
        <span className="text-xs font-bold uppercase tracking-wider">{meta.label}</span>
        <span className="text-xs font-mono opacity-70">· {f.check_type}</span>
      </div>
      <p className="mt-2 text-sm font-semibold">{f.rule}</p>
      <p className="mt-1 text-sm">{f.recommendation}</p>
      <details className="mt-2 text-xs opacity-80">
        <summary className="cursor-pointer">Measured evidence</summary>
        <pre className="mt-1 bg-white/50 border border-current/10 rounded p-2 overflow-auto">
          {JSON.stringify(f.measured_evidence, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function Kpi({ label, value, Icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600">
        <Icon size={16} />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">{label}</div>
        <div className="text-base font-semibold text-slate-900">{value}</div>
      </div>
    </div>
  );
}

export default function SeoAnalyzerPage() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState("single"); // "single" | "sitewide"
  const [crawl, setCrawl] = useState(null);
  const [crawlLoading, setCrawlLoading] = useState(false);
  const [vitals, setVitals] = useState(null);
  const [vitalsAlerts, setVitalsAlerts] = useState(null);

  usePageSeo({
    title: "SEO Analyzer — Deterministic Audit of Any Page | WebsiteTech.pro",
    description:
      "Scan any URL for 20+ measured SEO checks — canonical, title, meta description, headings, viewport, image alt, structured data, link graph and more. Every finding is grounded in HTTP + HTML evidence.",
  });
  useBreadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Resources", url: "/resources/guides" },
    { name: "SEO Analyzer", url: "/seo-analyzer" },
  ]);

  const run = async () => {
    const u = (url || "").trim();
    if (!u) { toast.error("Enter a URL first"); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.post(`${API}/seo-analyzer/scan`, { url: u });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally {
      setLoading(false);
    }
  };

  const runCrawl = async () => {
    const u = (url || "").trim();
    if (!u) { toast.error("Enter your site root URL first"); return; }
    setCrawlLoading(true); setCrawl(null);
    try {
      const { data } = await axios.post(`${API}/seo-analyzer/crawl`, {
        base_url: u, use_sitemap: true, max_urls: 30,
      });
      setCrawl(data);
      toast.success(`Crawled ${data.page_count} pages`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Crawl failed");
    } finally {
      setCrawlLoading(false);
    }
  };

  // Pull last 7 days of real-user web-vitals the first time this page mounts.
  useEffect(() => {
    axios.get(`${API}/web-vitals/summary?days=7`)
      .then((r) => setVitals(r.data))
      .catch(() => {});
    axios.get(`${API}/vitals-alerts/eval`)
      .then((r) => setVitalsAlerts(r.data))
      .catch(() => {});
  }, []);

  const sortedFindings = (result?.findings || []).slice().sort((a, b) =>
    SEV_ORDER.indexOf(a.severity) - SEV_ORDER.indexOf(b.severity),
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="pt-28 pb-20" data-testid="seo-analyzer-page">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand">
            Deterministic SEO Analyzer
          </p>
          <h1 className="mt-2 font-heading font-extrabold text-slate-900 text-4xl sm:text-5xl lg:text-6xl leading-tight">
            Scan any page.<br />
            <span className="text-brand">Measured evidence first.</span>
          </h1>
          <p className="mt-4 text-base text-slate-600 max-w-2xl">
            Every finding is grounded in HTTP + HTML evidence. No LLM invents a result — AI
            is only used (optionally) to explain what the measurement means. Fast, repeatable,
            and the same score for the same URL every time.
          </p>

          <div className="mt-8 flex gap-2">
            <button
              data-testid="seo-mode-single"
              onClick={() => setMode("single")}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${mode === "single" ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200"}`}
            >Single page</button>
            <button
              data-testid="seo-mode-sitewide"
              onClick={() => setMode("sitewide")}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${mode === "sitewide" ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200"}`}
            >Sitewide crawl</button>
          </div>

          <div className="mt-3 bg-white border border-slate-200 rounded-2xl p-4 flex flex-col sm:flex-row items-stretch gap-3 shadow-sm">
            <div className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg flex-1">
              <Globe size={16} className="text-slate-400" />
              <input
                data-testid="seo-url-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (mode === "single" ? run() : runCrawl())}
                placeholder={mode === "single" ? "https://your-website.com/page" : "https://your-website.com (reads /sitemap.xml)"}
                className="flex-1 outline-none text-sm"
              />
            </div>
            {mode === "single" ? (
              <button
                data-testid="seo-scan-btn"
                onClick={run}
                disabled={loading}
                className="btn-primary rounded-lg px-5 py-2.5 text-sm font-semibold whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Gauge size={16} />}
                {loading ? "Scanning…" : "Run deterministic scan"}
              </button>
            ) : (
              <button
                data-testid="seo-crawl-btn"
                onClick={runCrawl}
                disabled={crawlLoading}
                className="btn-primary rounded-lg px-5 py-2.5 text-sm font-semibold whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {crawlLoading ? <Loader2 size={16} className="animate-spin" /> : <Gauge size={16} />}
                {crawlLoading ? "Crawling…" : "Crawl entire site"}
              </button>
            )}
          </div>

          {result && (
            <div className="mt-10 space-y-6" data-testid="seo-result">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Kpi label="Score" value={`${result.score}/100`} Icon={Gauge} />
                <Kpi label="HTTP status" value={result.status} Icon={CheckCircle2} />
                <Kpi label="Response" value={`${result.elapsed_ms} ms`} Icon={Clock} />
                <Kpi label="Payload" value={`${Math.round((result.bytes || 0) / 1024)} KB`} Icon={FileCode2} />
              </div>

              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  Severity breakdown
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {SEV_ORDER.map((k) => {
                    const n = (result.severity_counts || {})[k] || 0;
                    if (!n) return null;
                    const m = SEV_META[k];
                    return (
                      <span
                        key={k}
                        data-testid={`seo-sev-${k}`}
                        className={`text-xs font-bold uppercase tracking-wider border px-2 py-1 rounded-full ${m.cls}`}
                      >
                        {m.label}: {n}
                      </span>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-3">
                {sortedFindings.length === 0 ? (
                  <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl p-6 text-center font-semibold">
                    No findings — every measured check passed.
                  </div>
                ) : (
                  sortedFindings.map((f) => <Finding key={f.finding_id} f={f} />)
                )}
              </div>

              <p className="text-xs text-slate-400">
                Scan ID: <span className="font-mono">{result.scan_id}</span> · generated at{" "}
                {new Date().toLocaleString()}. Measurement-first; AI explanation is optional and
                never a replacement for evidence.
              </p>
            </div>
          )}

          {crawl && (
            <div className="mt-10 space-y-6" data-testid="seo-crawl-result">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Kpi label="Pages crawled" value={crawl.page_count} Icon={Gauge} />
                <Kpi label="Avg score" value={`${crawl.rollup?.avg_score || 0}/100`} Icon={CheckCircle2} />
                <Kpi label="Critical pages" value={crawl.rollup?.pages_with_critical || 0} Icon={ShieldAlert} />
                <Kpi label="Broken links" value={crawl.rollup?.broken_links_total || 0} Icon={AlertTriangle} />
              </div>

              {(crawl.rollup?.duplicates || []).length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="seo-crawl-duplicates">
                  <div className="text-xs font-bold uppercase tracking-wider text-amber-700">
                    Duplicate-content candidates ({crawl.rollup.duplicates.length})
                  </div>
                  <ul className="mt-2 space-y-2 text-sm">
                    {crawl.rollup.duplicates.slice(0, 10).map((d, i) => (
                      <li key={i} className="text-amber-900">
                        <span className="font-semibold">{d.kind === "duplicate_title" ? "Shared title" : "Shared description"}:</span>{" "}
                        <span className="font-mono text-xs">{d.value}</span>
                        <div className="pl-3 mt-1 text-xs text-amber-700">
                          Appears on: {d.urls.slice(0, 3).map((u, j) => (
                            <span key={j}><a href={u} target="_blank" rel="noreferrer" className="underline">{u}</a>{j < Math.min(d.urls.length, 3) - 1 ? ", " : ""}</span>
                          ))}
                          {d.urls.length > 3 && ` + ${d.urls.length - 3} more`}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <div className="text-sm font-bold text-slate-900">Per-URL findings</div>
                  <div className="text-[11px] text-slate-400">crawl_id: <span className="font-mono">{crawl.crawl_id}</span></div>
                </div>
                <div className="max-h-[500px] overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 sticky top-0">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold">URL</th>
                        <th className="text-right px-3 py-2 font-semibold">Score</th>
                        <th className="text-right px-3 py-2 font-semibold">Crit</th>
                        <th className="text-right px-3 py-2 font-semibold">High</th>
                        <th className="text-right px-3 py-2 font-semibold">Med</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(crawl.pages || []).map((p, i) => {
                        const sev = (f) => p.findings.filter((x) => x.severity === f).length;
                        const scoreCls = p.score >= 80 ? "text-emerald-600" : p.score >= 60 ? "text-amber-600" : "text-rose-600";
                        return (
                          <tr key={i} className="border-t border-slate-100" data-testid={`seo-crawl-row-${i}`}>
                            <td className="px-4 py-2 truncate max-w-[400px]"><a href={p.url} target="_blank" rel="noreferrer" className="text-brand hover:underline">{p.url.replace(/^https?:\/\//, "")}</a></td>
                            <td className={`text-right px-3 py-2 font-bold ${scoreCls}`}>{p.score}</td>
                            <td className="text-right px-3 py-2 text-rose-600 font-semibold">{sev("critical") || "·"}</td>
                            <td className="text-right px-3 py-2 text-orange-600 font-semibold">{sev("high") || "·"}</td>
                            <td className="text-right px-3 py-2 text-amber-600 font-semibold">{sev("medium") || "·"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <p className="text-xs text-slate-400">
                Weekly scheduled crawls run every Monday 9am UTC against every registered domain — see{" "}
                <span className="font-mono">/api/seo-analyzer/health</span>.
              </p>
            </div>
          )}

          {vitals && Object.keys(vitals.by_device || {}).length > 0 && (
            <div className="mt-14" data-testid="seo-webvitals">
              <div className="flex items-center gap-2">
                <Activity size={16} className="text-brand" />
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand">
                  Real-user Web Vitals · last 7 days (p75)
                </p>
              </div>
              <p className="mt-2 text-sm text-slate-500">
                Field data streamed from real visitors — complements the static evidence above.
                Buckets follow Google's p75 thresholds.
              </p>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(vitals.by_device).map(([device, metrics]) => (
                  <div
                    key={device}
                    className="bg-white border border-slate-200 rounded-xl p-4"
                    data-testid={`webvitals-device-${device}`}
                  >
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                      {device}
                    </div>
                    <div className="mt-2 grid grid-cols-5 gap-2 text-center">
                      {["LCP", "INP", "CLS", "FCP", "TTFB"].map((m) => {
                        const d = metrics[m];
                        if (!d) return (
                          <div key={m} className="opacity-40">
                            <div className="text-[10px] text-slate-400">{m}</div>
                            <div className="text-xs">—</div>
                          </div>
                        );
                        const ratingCls =
                          d.rating === "good" ? "text-emerald-600" :
                          d.rating === "needs_improvement" ? "text-amber-600" :
                          d.rating === "poor" ? "text-rose-600" : "text-slate-500";
                        return (
                          <div key={m}>
                            <div className="text-[10px] font-semibold text-slate-500">{m}</div>
                            <div className={`text-sm font-bold ${ratingCls}`}>
                              {d.p75}{m === "CLS" ? "" : "ms"}
                            </div>
                            <div className="text-[9px] text-slate-400">{d.n} samples</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              {(vitals.worst_lcp_paths || []).length > 0 && (
                <div className="mt-4 bg-white border border-slate-200 rounded-xl p-4">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Slowest paths by LCP p75
                  </div>
                  <ul className="mt-2 space-y-1 text-sm">
                    {vitals.worst_lcp_paths.map((w, i) => (
                      <li key={i} className="flex items-center justify-between" data-testid={`webvitals-worst-${i}`}>
                        <span className="font-mono text-xs text-slate-700">{w.path}</span>
                        <span className="text-xs text-slate-400">{w.lcp_p75} ms · {w.samples} samples</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {vitalsAlerts && (
            <div className="mt-10" data-testid="vitals-alerts-preview">
              <div className="flex items-center gap-2">
                <ShieldAlert size={16} className={vitalsAlerts.count > 0 ? "text-rose-600" : "text-emerald-600"} />
                <p className={`text-xs font-bold uppercase tracking-[0.18em] ${vitalsAlerts.count > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                  Chronic LCP alert preview · would fire now
                </p>
              </div>
              <p className="mt-2 text-sm text-slate-500">
                Previews what the Monday 9am UTC vitals cron would email. A path qualifies after
                two consecutive weeks in the <strong>Poor</strong> bucket (p75 &gt; 4000ms).
                Nothing is emailed or persisted when you view this card.
              </p>
              {vitalsAlerts.count === 0 ? (
                <div
                  className="mt-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl p-4 text-sm font-semibold flex items-center gap-2"
                  data-testid="vitals-alerts-empty"
                >
                  <CheckCircle2 size={16} /> No chronic LCP paths right now — nothing would alert this week.
                </div>
              ) : (
                <div className="mt-4 bg-white border border-rose-200 rounded-xl overflow-hidden" data-testid="vitals-alerts-table">
                  <table className="w-full text-sm">
                    <thead className="bg-rose-50 text-[11px] uppercase tracking-wider text-rose-800">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold">Path</th>
                        <th className="text-left px-3 py-2 font-semibold">Device</th>
                        <th className="text-right px-3 py-2 font-semibold">This week p75</th>
                        <th className="text-right px-4 py-2 font-semibold">Last week p75</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vitalsAlerts.candidates.map((a, i) => (
                        <tr key={i} className="border-t border-rose-100" data-testid={`vitals-alert-row-${i}`}>
                          <td className="px-4 py-2 font-mono text-xs text-slate-700">{a.path}</td>
                          <td className="px-3 py-2 text-xs text-slate-500">{a.device}</td>
                          <td className="text-right px-3 py-2 font-bold text-rose-600">{a.current_week_p75} ms</td>
                          <td className="text-right px-4 py-2 text-rose-500">{a.prev_week_p75} ms</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
